import Link from "next/link";
import { useEffect, useRef, useState, useCallback } from "react";

/* ───────────────────── conversation data ───────────────────── */

const conversations = [
  {
    id: 0,
    segment: "Government",
    question:
      "Which local authorities have the worst transport accessibility scores?",
    answer:
      "County Durham, Lincolnshire, and North Yorkshire score lowest. Rural coverage gap is the primary driver in all three.",
    cardTitle: "Transport Accessibility",
    rows: [
      { label: "County Durham", value: "34/100", status: "high" },
      { label: "Lincolnshire", value: "38/100", status: "high" },
      { label: "North Yorkshire", value: "41/100", status: "rising" },
      { label: "Shropshire", value: "47/100", status: "rising" },
    ],
  },
  {
    id: 1,
    segment: "NGO",
    question:
      "Which of our programmes have the lowest outcome rates this quarter?",
    answer:
      'Clean Water (Haiti), Nutrition (DRC), and Shelter (Sudan) are underperforming targets. Clean Water is 34% below Q2.',
    cardTitle: "Programme Outcomes",
    rows: [
      { label: "Clean Water — Haiti", value: "−34%", status: "high" },
      { label: "Nutrition — DRC", value: "−21%", status: "high" },
      { label: "Shelter — Sudan", value: "−18%", status: "rising" },
      { label: "Education — Mali", value: "+6%", status: "up" },
    ],
  },
  {
    id: 2,
    segment: "Enterprise",
    question: "Which regions have the lowest data quality scores and why?",
    answer:
      "North East (42%), West Midlands (51%), South West (58%). Main issues: missing metadata and outdated resources.",
    cardTitle: "Data Quality",
    rows: [
      { label: "North East", value: "42%", status: "high" },
      { label: "West Midlands", value: "51%", status: "high" },
      { label: "South West", value: "58%", status: "rising" },
      { label: "East Midlands", value: "74%", status: "up" },
    ],
  },
  {
    id: 3,
    segment: "CKAN / Technical",
    question:
      "Which datasets get the most views but have the least complete metadata?",
    answer:
      "3 high-traffic datasets have incomplete metadata — combined 14,200 monthly views. Fixing these would improve discoverability significantly.",
    cardTitle: "Priority Fixes",
    rows: [
      { label: "Road Accidents 2023", value: "6,400 views", status: "high" },
      { label: "Housing Register", value: "4,900 views", status: "high" },
      {
        label: "Planning Applications",
        value: "2,900 views",
        status: "rising",
      },
      { label: "Metadata score avg", value: "31%", status: "high" },
    ],
  },
];

/* ─────────── status badge helper ─────────── */

function StatusBadge({ status }) {
  const map = {
    high: { dot: "bg-red-500", text: "text-red-500", label: "High" },
    rising: {
      dot: "bg-orange-400",
      text: "text-orange-400",
      label: "Rising",
    },
    up: { dot: "bg-emerald-500", text: "text-emerald-500", label: "Up" },
  };
  const s = map[status] || map.high;
  return (
    <span className={`flex items-center gap-1 text-[0.65rem] font-semibold ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/* ─────────── result card ─────────── */

function ResultCard({ title, rows }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100">
        <span className="text-orange-500 font-bold text-[0.65rem] tracking-wider uppercase">
          {title}
        </span>
      </div>
      <div className="divide-y divide-gray-50">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between px-3 py-1.5"
          >
            <span className="text-gray-700 text-[0.7rem] font-medium truncate pr-2">
              {row.label}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-gray-900 text-[0.7rem] font-semibold">
                {row.value}
              </span>
              <StatusBadge status={row.status} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── animated phone chat ─────────── */

/*
  Phase flow (all elements always mounted — only opacity/transform change):

  "typing"    → question bubble + typing dots appear together      (1400ms)
  "answer"    → typing dots fade, answer + card slide in           (4300ms)
  "fadeout"   → everything fades out together                      (300ms)
  → next conversation starts from "typing"
  Total per conversation: 6000ms

  Clicking a dot jumps immediately and resets.
*/

const PHASE_TIMING = {
  typing: 1400,    // ms question + typing indicator are visible together
  answer: 4300,    // ms answer + card are visible before fading
  fadeout: 300,    // ms fade-out duration
};

function PhoneChat() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [phase, setPhase] = useState("typing");
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback((nextPhase, delay) => {
    clearTimer();
    timerRef.current = setTimeout(() => setPhase(nextPhase), delay);
  }, [clearTimer]);

  // Phase machine
  useEffect(() => {
    switch (phase) {
      case "typing":
        scheduleNext("answer", PHASE_TIMING.typing);
        break;
      case "answer":
        scheduleNext("fadeout", PHASE_TIMING.answer);
        break;
      case "fadeout":
        timerRef.current = setTimeout(() => {
          setActiveIdx((prev) => (prev + 1) % conversations.length);
          setPhase("typing");
        }, PHASE_TIMING.fadeout);
        break;
      default:
        break;
    }
    return clearTimer;
  }, [phase, scheduleNext, clearTimer]);

  // Dot click handler
  const jumpTo = useCallback((idx) => {
    clearTimer();
    setActiveIdx(idx);
    setPhase("typing");
  }, [clearTimer]);

  const conv = conversations[activeIdx];

  // Derive visibility booleans from phase
  const questionVisible = phase === "typing" || phase === "answer";
  const typingVisible = phase === "typing";
  const answerVisible = phase === "answer";
  const fadingOut = phase === "fadeout";

  // Inline transition style helper
  const trans = (duration = 350, delay = 0) => ({
    transition: `opacity ${duration}ms ease ${delay}ms, transform ${duration}ms ease ${delay}ms`,
  });

  const hidden = { opacity: 0, transform: "translateY(10px)" };
  const visible = { opacity: 1, transform: "translateY(0)" };

  return (
    <div className="flex flex-col items-center">
      {/* Device frame */}
      <div className="relative rounded-[2.5rem] bg-gradient-to-b from-[#3a3a3a] to-[#2a2a2a] p-[6px] shadow-2xl w-[300px] sm:w-[320px]">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          <div className="h-7 w-36 rounded-b-2xl bg-[#1a1a1a] flex items-center justify-center">
            <div className="w-16 h-[5px] rounded-full bg-[#333]" />
          </div>
        </div>

        {/* Screen */}
        <div className="relative rounded-[2.1rem] bg-white overflow-hidden flex flex-col h-[480px] sm:h-[520px]">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-8 pb-1">
            <span className="text-gray-800 text-[0.65rem] font-semibold">
              9:41
            </span>
            <div className="flex items-center gap-1.5 text-[0.6rem] text-gray-600 font-medium">
              <span>•••</span>
              <span>WiFi</span>
              <svg
                className="w-3 h-3 text-green-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z" />
              </svg>
            </div>
          </div>

          {/* App header */}
          <div className="px-4 pb-2.5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <div>
                  <p className="font-semibold text-gray-900 text-sm leading-none">
                    Data Assistant
                  </p>
                  <p className="text-gray-400 text-[0.65rem] mt-0.5">
                    Powered by Datopian AI
                  </p>
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-orange-400 text-white text-[0.6rem] font-bold flex items-center justify-center">
                D
              </div>
            </div>
          </div>

          {/* Messages area — all elements always mounted, visibility via CSS */}
          <div className="flex-1 flex flex-col gap-0 justify-end px-4 pb-2 overflow-hidden">

            {/* Question bubble */}
            <div
              className="flex items-end gap-2 flex-row-reverse mb-2.5"
              style={{
                ...trans(350),
                ...(questionVisible && !fadingOut ? visible : hidden),
              }}
            >
              <div className="w-6 h-6 rounded-full bg-orange-400 text-white text-[0.5rem] font-bold flex items-center justify-center flex-shrink-0">
                U
              </div>
              <div className="max-w-[82%] px-3 py-2 rounded-2xl rounded-br-sm bg-orange-400 text-white text-[0.78rem] leading-relaxed">
                {conv.question}
              </div>
            </div>

            {/* Typing indicator */}
            <div
              className="flex items-end gap-2 mb-2.5"
              style={{
                ...trans(280),
                ...(typingVisible ? visible : hidden),
                ...(typingVisible ? {} : { position: "absolute", pointerEvents: "none" }),
              }}
            >
              <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-[0.5rem] font-bold flex items-center justify-center flex-shrink-0">
                AI
              </div>
              <div className="inline-flex gap-1.5 items-center px-3.5 py-2.5 bg-gray-100 rounded-2xl rounded-bl-sm">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                <span
                  className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.15s" }}
                />
                <span
                  className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.3s" }}
                />
              </div>
            </div>

            {/* AI answer + result card */}
            <div
              className="flex flex-col gap-2"
              style={{
                ...trans(400, 80),
                ...(answerVisible ? visible : hidden),
                ...(!answerVisible && !fadingOut ? { maxHeight: 0, overflow: "hidden" } : { maxHeight: 600 }),
              }}
            >
              <div className="flex items-end gap-2">
                <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-[0.5rem] font-bold flex items-center justify-center flex-shrink-0">
                  AI
                </div>
                <div className="max-w-[82%] px-3 py-2 rounded-2xl rounded-bl-sm bg-gray-100 text-gray-700 text-[0.78rem] leading-relaxed">
                  <span className="text-orange-500 font-semibold">
                    {conv.answer.split(".")[0]}.
                  </span>{" "}
                  {conv.answer.split(".").slice(1).join(".").trim()}
                </div>
              </div>
              <div className="ml-8">
                <ResultCard title={conv.cardTitle} rows={conv.rows} />
              </div>
            </div>
          </div>

          {/* Input bar */}
          <div className="px-3 pb-4 pt-1.5">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-2">
              <span className="flex-1 text-[0.78rem] text-gray-400 truncate">
                {conv.question.slice(0, 40)}…
              </span>
              <button
                className="bg-orange-400 border-none rounded-xl w-7 h-7 flex items-center justify-center flex-shrink-0"
                aria-label="Send"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Home indicator */}
          <div className="flex justify-center pb-1.5">
            <div className="w-24 h-1 rounded-full bg-gray-300" />
          </div>
        </div>
      </div>

      {/* Dot navigation */}
      <div className="flex items-center gap-2.5 mt-5">
        {conversations.map((c, i) => (
          <button
            key={c.id}
            onClick={() => jumpTo(i)}
            aria-label={`View ${c.segment} conversation`}
            className={`rounded-full transition-all duration-300 ${
              i === activeIdx
                ? "w-3 h-3 bg-orange-400"
                : "w-2.5 h-2.5 bg-gray-500 hover:bg-gray-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────── main strip component ─────────── */

export default function AIDataStrip() {
  return (
    <section className="w-full bg-[#1B2030] overflow-hidden font-['Inter']">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 py-20 sm:py-28 lg:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left — text content */}
          <div className="flex-1 max-w-2xl">
            <span className="text-orange-400 font-bold text-sm tracking-[0.18em] uppercase flex items-center gap-3 mb-6">
              <span className="w-8 h-[2px] bg-orange-400 inline-block" />
              AI Data Services
            </span>

            <h2 className="main-top-title !text-white !font-['Inter']">
              The answer is in your data. Getting to it reliably is the hard
              part.
            </h2>

            <div className="mt-6">
              <span className="inline-block px-5 py-2 rounded-full border border-gray-600 text-gray-300 text-sm font-['Inter']">
                We&apos;ve been solving exactly that — since 2004.
              </span>
            </div>

            <p className="mt-8 body-text !text-gray-400 !text-base !leading-relaxed max-w-xl">
              Getting a trusted answer from your data sounds simple. In practice
              it means dealing with inconsistent formats, legacy systems, missing
              metadata, and sources that don&apos;t talk to each other.
              That&apos;s why most AI data tools disappoint — they assume the
              data is ready. It isn&apos;t. Datopian has spent 20 years making
              real-world data trustworthy and queryable — for the US government,
              the World Bank, the NHS, and organisations across six continents.
              We know what your data actually looks like. And we know how to make
              AI work reliably — not just in demos. Working prototype in 5 days.
            </p>

            <div className="mt-10">
              <Link
                href="/solutions/ai-data-services"
                className="btn-primary"
              >
                Start a 5-Day Prototype
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ml-2"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Right — animated phone */}
          <div className="flex-shrink-0">
            <PhoneChat />
          </div>
        </div>
      </div>
    </section>
  );
}
